import React, {useEffect, useState, useContext} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../utils/apiBaseUrl';
import { ButtonGroup, ButtonToolbar, Button, Form, Pagination } from 'react-bootstrap';
import './EnterPicks.css';
import CurrentWeekNum from './../../utils/CurrentWeekNum';
import IsEligible from './../../utils/IsEligible';
import teamIcons from './../../utils/teamIcons';
import teamColors from './../../utils/teamColors';
import { AccountContext } from './../../components/UserPool/Account';
import PastPicksCard from '../../components/PastPicksCard/PastPicksCard';
import DeadlineTooltip from '../../components/DeadlineTooltip/DeadlineTooltip';
import { Lock, CalendarWeek } from 'react-bootstrap-icons';
import GameDeadlineTitle from '../../utils/GameDeadlineTitle';
import PickReminderSwitch from '../../components/PickReminderSwitch/PickReminderSwitch'
import AlertModal from '../../components/AlertModal/AlertModal';
import TeamAvailabilityTracker from '../../components/TeamAvailabilityTracker/TeamAvailabilityTracker';
import TeamScheduleModal from '../../components/TeamScheduleModal/TeamScheduleModal';

// API article https://medium.com/swlh/skip-lambda-save-data-to-dynamodb-directly-using-api-gateway-process-later-with-streams-dab2ceef9a9d
function EnterPicks({year}) {
    const { getSession } = useContext(AccountContext);
    const [newSession, setNewSession] = useState(undefined);

    let navigate = useNavigate(); 

    // const [isTeamSelectedArr, setIsTeamSelectedArr] = useState(Array(18).fill(false));
    const [currentPickArr, setCurrentPickArr] = useState(Array(18).fill(''));
    const [activeButtonArr, setActiveButtonArr] = useState(Array(18).fill(Array(32).fill(null)));

    // API - current week of season (corrected once the server tells us the real
    // value -- see the serverCurrentWeek effect below; CurrentWeekNum(year) is
    // only a same-render placeholder and is unreliable for 2026+ seasons)
    const [activePage, setActivePage] = useState(CurrentWeekNum(year));
    const [serverCurrentWeek, setServerCurrentWeek] = useState(null);

    const [currReminderDecision, setCurrReminderDecision] = useState(false);

    const [locked, setLocked] = useState(Array(18).fill(false));
    const [currWeekEligible, setCurrWeekEligible] = useState(true);

    const [matchupsArr, setMatchupsArr] = useState([[]]);
    const [deadlinesArr, setDeadlinesArr] = useState([[]]);
    const [deadlineLabelsArr, setDeadlineLabelsArr] = useState(null);
    const [spreadsObj, setspreadsObj] = useState([]);
    const [showSubmitButton, setShowSubmitButton] = useState(false);

    const [userPicks, setUserPicks] = useState([{'Team':''}]);
    const [userStats, setUserStats] = useState({});

    const [showAlertModal, setShowAlertModal] = useState([false, '', ''])

    // Whole-season schedule (all 18 weeks), fetched once -- powers the team
    // availability tracker's bye-week tags and the click-a-team schedule modal.
    // Empty for years without a dynamically-synced schedule (2022-2025); both
    // features just have nothing to show in that case.
    const [fullSchedule, setFullSchedule] = useState([]);
    const [scheduleModalTeam, setScheduleModalTeam] = useState(null);

    useEffect(() => {
        getSession(setNewSession)
          .then((session) => {
            console.log('Session Activated');
          })
          .catch((err) => {
            console.log('Session: ', err);
          });
        
      }, []);

    // Has this week's main ('normal', i.e. Sunday-noon-CT) deadline passed?
    // IsEligible can only resolve a per-game ISO timestamp (see deadlinesArr
    // usage below) -- there's no per-game deadline to compare for this
    // week-level check, so 'normal' always fell through to the legacy
    // hardcoded date tables, which have no 2026 branch. Same fix already
    // applied to CurrentWeek.js/Admin.js: derive it from the server's
    // currentWeek/eligibility instead once loaded.
    function isWeekNormalDeadlinePassed(weekNum) {
        if (serverCurrentWeek == null) return !IsEligible(weekNum, 'normal', year);
        if (weekNum < serverCurrentWeek) return true;
        if (weekNum > serverCurrentWeek) return false;
        return !currWeekEligible;
    }

    function establishPicks(picks) {
        setUserPicks(picks);

        let updatedPicks = {...currentPickArr}
        let tempVal;
        picks.forEach((val,i) => {
            tempVal = Object.keys(val)[0];
            if(!tempVal.includes('Team')) updatedPicks[i] = tempVal;
        }) 
        
        setCurrentPickArr(updatedPicks);
    }

    function updateLockedWeeks(week, picks) {
        let newLocked = [...locked]

        for(let i = 0; i < 18; ++i) {
            const initialEligibility = !isWeekNormalDeadlinePassed(i+1)
            if (!initialEligibility || (i+1 === week && !currWeekEligible)) {
                newLocked[i] = true
                continue
            }

            if (initialEligibility && Object.keys(picks[i])[0].includes('Team')) {
                continue
            }

            if (matchupsArr.length === 0 || deadlinesArr.length === 0) return
            const deadlineIndex = matchupsArr[i].findIndex((m) => m.includes(Object.keys(picks[i])[0]))
            const currDeadline = deadlinesArr[i][deadlineIndex]

            eligibility = IsEligible(i+1, currDeadline, year)
            newLocked[i] = !eligibility
        }
        setLocked(newLocked)
    }

    async function getFullSchedule() {
        const user = newSession['accessToken']['payload']['username']
        const matchupUsername = user.includes('passwordless') ? user.split('-passwordless')[0] : user
        await axios.get(`${API_BASE_URL}/get-full-schedule`, {
            headers: {
                Authorization: newSession['idToken']['jwtToken'],
                'Content-Type': 'application/json'
            },
            params: {'username': matchupUsername, year}
        }).then((response) => {
            setFullSchedule(response['data']['games'] || []);
        }).catch((error) => {
            console.log(error);
            // Non-critical: the availability tracker/schedule modal just show
            // less (no bye-week tags) rather than blocking the page.
        });
    }

    // The whole-season schedule is only needed for the availability tracker's
    // bye tags and the schedule modal -- neither is visible/used on first
    // paint (the tracker starts collapsed, the modal starts closed), so fetch
    // it lazily on first actual need instead of competing with the matchups/
    // picks calls that block the page's critical content. fullScheduleRequested
    // guards against re-fetching on every subsequent expand/click.
    const fullScheduleRequested = React.useRef(false);
    function ensureFullSchedule() {
        if (fullScheduleRequested.current || newSession === undefined) return;
        fullScheduleRequested.current = true;
        getFullSchedule();
    }

    function openScheduleModal(team) {
        ensureFullSchedule();
        setScheduleModalTeam(team);
    }

    async function getAllMatchups(weekOverride) {
        // Use the server's own currentWeek once we know it (set after the first
        // call below); CurrentWeekNum(year) is only a same-render placeholder for
        // the very first request and is unreliable for 2026+ seasons.
        const week = weekOverride ?? (serverCurrentWeek ?? CurrentWeekNum(year))
        const user = newSession['accessToken']['payload']['username']
        const matchupUsername = user.includes('passwordless') ? user.split('-passwordless')[0] : user
        await axios.get(`${API_BASE_URL}/get-all-matchups`, {
            headers: {
                Authorization: newSession['idToken']['jwtToken'],
                'Content-Type': 'application/json'
            },
            params: {'username': matchupUsername, year, 'week': week}
        }).then((response) => {
            console.log(response['data'])
            if(response['data']['eligibility'] === true) {
                console.log('Deadline not passed')
            } else {
                console.log('Deadline passed')
                setCurrWeekEligible(false)
            }

            if (response['data']['currentWeek'] != null && response['data']['currentWeek'] !== week) {
                // Our initial week guess was wrong (2026+ seasons) -- correct the
                // active page and refetch once with the real week so spreads line
                // up, instead of looping indefinitely.
                setServerCurrentWeek(response['data']['currentWeek']);
                setActivePage(response['data']['currentWeek']);
                getAllMatchups(response['data']['currentWeek']);
                return;
            }
            if (response['data']['currentWeek'] != null) {
                setServerCurrentWeek(response['data']['currentWeek']);
            }

            setMatchupsArr(response['data']['matchups']);
            setDeadlinesArr(response['data']['deadlines']);
            setDeadlineLabelsArr(response['data']['deadlineLabels'] ?? null);
            setspreadsObj(response['data']['spreads']);
        }).catch((error) => {
            console.log(error);
            setShowAlertModal([true, 'Unable to get matchups. Refresh the page and try again.', 'We\'ve hit a snag'])
        });
    }

    async function getUserPicks() {
        const user = newSession['accessToken']['payload']['username']
        const matchupUsername = user.includes('passwordless') ? user.split('-passwordless')[0] : user
        const fullname = newSession['idToken']['payload']['name']
        const email = newSession['idToken']['payload']['email']
        console.log("EMAIL IN SEND", email)
        await axios.get(`${API_BASE_URL}/userinfo`, {
            headers: {
                Authorization: newSession['idToken']['jwtToken'],
                'Content-Type': 'application/json',
            },
            params: {'user': matchupUsername, 'name': fullname, year, email: email}
        }).then((response) => {
            setUserStats(response['data']);
            establishPicks(response['data']['user_picked_teams']);
            updateLockedWeeks(activePage, response['data']['user_picked_teams'])
            setShowSubmitButton(true);
        }).catch((error) => {
            console.log(error); 
            setShowAlertModal([true, 'Unable to get picks. Refresh the page and try again.', 'We\'ve hit a snag'])
        });
    }

    async function getUserReminderDecision() {
        const user = newSession['accessToken']['payload']['username']
        const matchupUsername = user.includes('passwordless') ? user.split('-passwordless')[0] : user
        await axios.get(`${API_BASE_URL}/get-reminder`, {
            headers: {
                Authorization: newSession['idToken']['jwtToken'],
                'Content-Type': 'application/json',
            },
            params: {'username': matchupUsername, year}
        }).then((response) => {
            setCurrReminderDecision(response['data']['reminder']);
        }).catch((error) => {
            console.log(error); 
        });
    }

    async function setUserReminderDecision(event) {
        // event.preventDefault();
        const decision = event.target.checked;
        const email = newSession['idToken']['payload']['email'];
        const user = newSession['accessToken']['payload']['username']
        const matchupUsername = user.includes('passwordless') ? user.split('-passwordless')[0] : user
        setCurrReminderDecision(decision)
        await axios.put(`${API_BASE_URL}/set-reminder`, 
            {
                'username': matchupUsername,
                'email': email,
                'decision': decision
            },
            {
                headers: {
                    Authorization: newSession['idToken']['jwtToken'],
                    'Content-Type': 'application/json'
                },
                params: {year}
            }
        ).then((response) => {
            const dynamicMsg = decision ? "You will" : "You will no longer no longer"
            const dynamicOnOff = decision ? "on" : "off"
            console.log(dynamicOnOff)
            setShowAlertModal([true, `Successfully turned email reminders ${dynamicOnOff}. ${dynamicMsg} receive a weekly reminder at 11:30am CT.`, `Reminders Turned ${dynamicOnOff.charAt(0).toUpperCase() + dynamicOnOff.slice(1)}`])
        }).catch((error) => {
            console.log(error);
            setShowAlertModal([true, 'Unable to set reminder alert. Please refresh page and try again.', 'We\ve Hit a Snag'])
        });
    }

    async function submitUserPick(event) {
        event.preventDefault();
        const user = newSession['accessToken']['payload']['username']
        const matchupUsername = user.includes('passwordless') ? user.split('-passwordless')[0] : user
        await axios.put(`${API_BASE_URL}/submit-pick`, 
            {
                'username': matchupUsername,
                'weekNum': activePage,
                'pick': currentPickArr[activePage-1] || 'Team' + activePage
            },
            {
                headers: {
                    Authorization: newSession['idToken']['jwtToken'],
                    'Content-Type': 'application/json'
                },
                params: {year}
            }
        ).then((response) => {
            window.location.reload();
        }).catch((error) => {
            console.log(error);
            if(error['response']['status'] === 403) {
                setShowAlertModal([true, 'The deadline has passed for this week and your pick cannot be submitted.', 'Deadline Passed'])
            }
            else {
                setShowAlertModal([true, 'Unable to submit pick. Please text 847-630-2489 to submit.', 'We\ve Hit a Snag'])
            }
        });
    }


    useEffect(() => {
        if(newSession !== undefined) {
            getAllMatchups()
        }
      }, [newSession]);

    useEffect(() => {
        if(newSession !== undefined) {
            console.log('getting user picks')
            getUserPicks()
        }
      }, [deadlinesArr]);

    useEffect(() => {
        if(newSession !== undefined) {
            getUserReminderDecision()
        }
    }, [locked]);
    
    let items = [];
    for(let i = 1; i < 19; ++i) {

        items.push(
            <Pagination.Item key={i} active={i === activePage} onClick={(event)=>{
                setActivePage(i);
            }}>
                {i}
            </Pagination.Item>
        );
    }

    function handleTeamChosen(event, i) {
        let buttonText = event.target.innerText;
        // let newTeamSelectedArr = {...isTeamSelectedArr}
        if(buttonText === undefined) {
            const iconText = (event.target.nearestViewportElement) ? event.target.nearestViewportElement.id : event.target.id;
            if(iconText === undefined) {
                return;
            }
            buttonText = iconText;
        }

        const currName = buttonText.split('(')[0].trim();
        let trimmedName = currName.trim();
        trimmedName = trimmedName.split(' ')[0]
        const unclickTeam = trimmedName === currentPickArr[activePage-1].trim();

        let newActiveArr = {...activeButtonArr}
        let newActiveSubArr = new Array(32).fill(false);
        newActiveSubArr[i] = unclickTeam ? false : true;
        newActiveArr[activePage-1] = newActiveSubArr;
        setActiveButtonArr(newActiveArr);

        // newTeamSelectedArr[activePage-1] = unclickTeam ? false : true;
        // setIsTeamSelectedArr(newTeamSelectedArr);

        let newPickArr = {...currentPickArr};
        if(unclickTeam) newPickArr[activePage-1] = ''
        else newPickArr[activePage-1] = currName;
        setCurrentPickArr(newPickArr);
    }

    let arrButtons = [];
    let weeklyButtons = [];
    let prevDeadline = '';
    let isFirstTeamPicked, isSecondTeamPicked, buttonColor, currKey, currVal, currDeadline, eligibility;
    if(!locked[activePage-1] && matchupsArr.length > 1 && userPicks.length > 1) {
        const currWeek = serverCurrentWeek ?? CurrentWeekNum(year)
        for (let i = 0; i < matchupsArr[activePage-1].length * 2; i+=2) { 
            const matchup = matchupsArr[activePage-1][i/2];
            let firstTeam = matchup.split(' ')[0];
            let secondTeam = matchup.split(' ')[2];
    
            var NFLTeamOne = teamIcons[firstTeam];
            var NFLTeamTwo = teamIcons[secondTeam];
    
            currKey = Object.keys(userPicks[activePage-1])[0];
            currVal = Object.values(userPicks[activePage-1])[0];
    
            buttonColor = (typeof(currVal) === 'string') ? 'outline-primary' : (currVal ? 'outline-success' : 'outline-danger');

            // Undecided picks (result not in yet) get a subtle accent in that
            // team's own brand color instead of the generic orange -- once a
            // result is known the button switches to win/success or
            // loss/danger green/red, where semantic color matters more than
            // team identity, so this only applies to buttonColor === 'outline-primary'.
            const teamAccentStyle = (team) => buttonColor === 'outline-primary'
                ? {'--pick-team-color': teamColors[team]}
                : undefined;

            isFirstTeamPicked = userPicks.filter(function (key) {
                                    return key.hasOwnProperty(firstTeam);
                                }).length > 0;
    
            isSecondTeamPicked = userPicks.filter(function (key) {
                                    return key.hasOwnProperty(secondTeam);
                                }).length > 0;
            
            currDeadline = deadlinesArr.length > 1 ? deadlinesArr[activePage-1][i/2] : '';
            const currDeadlineLabel = deadlineLabelsArr ? deadlineLabelsArr[activePage-1][i/2] : GameDeadlineTitle(currDeadline, activePage);
            if(currDeadline !== prevDeadline) {
                arrButtons.push(
                    <span key={i+'-span'} className={'game-day-header'}> {currDeadlineLabel} </span>
                )
                
            } 
    
            let spread = spreadsObj[matchup]
            if(spread && spread[spread.length - 1] === '0') {
                spread = spread.split('.')[0]
            }
            
            const homeDisplay = spread ? `${secondTeam} (${spread})` : secondTeam
            prevDeadline = currDeadline;
            eligibility = IsEligible(activePage, currDeadline, year);

            let firstTeamButton;
            if((activePage < currWeek || !eligibility) && currKey !== firstTeam) {
                firstTeamButton = <Button key={i} variant='outline-secondary' className='pick-select-button' disabled> <NFLTeamOne/>{firstTeam}</Button>
            } else if((activePage < currWeek || !eligibility) && currKey !== firstTeam) {
                firstTeamButton = <Button key={i} variant={buttonColor} className='pick-select-button' disabled> <NFLTeamOne/>{firstTeam}</Button>
            } else if(activePage < currWeek && currKey === firstTeam) {
                firstTeamButton = <Button key={i} variant={buttonColor} className='pick-select-button' style={teamAccentStyle(firstTeam)} active> <NFLTeamOne/> {firstTeam}</Button>
            } else if(isFirstTeamPicked && currKey !== firstTeam) {
                firstTeamButton = <Button key={i} variant='outline-secondary' className='pick-select-button' disabled> <NFLTeamOne/>{firstTeam}</Button>
            } else if (firstTeam !== currKey) {
                firstTeamButton = <Button key={i} variant='outline-primary' className='pick-select-button' active={(activeButtonArr[activePage-1][i] === null) ? false : activeButtonArr[activePage-1][i]} onClick={(event)=>handleTeamChosen(event,i)}> <NFLTeamOne/> {firstTeam} </Button>
            } else if(!eligibility) {
                firstTeamButton = <Button key={i} variant={buttonColor} className='pick-select-button' style={teamAccentStyle(firstTeam)} active={true}> <NFLTeamOne/> {firstTeam} </Button>
            } else {
                firstTeamButton = <Button key={i} variant={buttonColor} className='pick-select-button' style={teamAccentStyle(firstTeam)} active={(activeButtonArr[activePage-1][i] === null) ? true : activeButtonArr[activePage-1][i]} onClick={(event)=>handleTeamChosen(event,i)}> <NFLTeamOne/> {firstTeam} </Button>
            }

            let secondTeamButton;
            if((activePage < currWeek || !eligibility) && currKey !== secondTeam) {
                secondTeamButton = <Button key={i+1} variant='outline-secondary' className='pick-select-button' disabled> <NFLTeamTwo/> {homeDisplay}</Button>
            } else if((activePage < currWeek || !eligibility) && currKey !== secondTeam) {
                secondTeamButton = <Button key={i+1} variant={buttonColor} className='pick-select-button' disabled> <NFLTeamTwo/> {homeDisplay}</Button>
            } else if(activePage < currWeek && currKey === secondTeam) {
                secondTeamButton = <Button key={i+1} variant={buttonColor} className='pick-select-button' style={teamAccentStyle(secondTeam)} active> <NFLTeamTwo/> {homeDisplay}</Button>
            } else if(isSecondTeamPicked && currKey !== secondTeam) {
                secondTeamButton = <Button key={i+1} variant='outline-secondary' className='pick-select-button' disabled> <NFLTeamTwo/> {homeDisplay}</Button>
            } else if (secondTeam !== currKey) {
                secondTeamButton = <Button key={i+1} variant='outline-primary' className='pick-select-button' active={(activeButtonArr[activePage-1][i+1] === null) ? false : activeButtonArr[activePage-1][i+1]} onClick={(event)=>handleTeamChosen(event,i+1)}> <NFLTeamTwo/> {homeDisplay} </Button>
            } else if(!eligibility) {
                secondTeamButton = <Button key={i+1} variant={buttonColor} className='pick-select-button' style={teamAccentStyle(secondTeam)} active={true}> <NFLTeamTwo/> {homeDisplay} </Button>
            } else {
                secondTeamButton = <Button key={i+1} variant={buttonColor} className='pick-select-button' style={teamAccentStyle(secondTeam)} active={(activeButtonArr[activePage-1][i+1] === null) ? true : activeButtonArr[activePage-1][i+1]} onClick={(event)=>handleTeamChosen(event,i+1)}> <NFLTeamTwo/> {homeDisplay} </Button>
            }

            // Each matchup gets its own full-width row (not just alternating
            // grid cells) so the two teams always sit opposite each other,
            // regardless of how many matchups share the week or how wide the
            // viewport is -- see EnterPicks.css .matchup-row.
            arrButtons.push(
                <div className='matchup-row' key={i+'-row'}>
                    <div className='matchup-tile'>
                        {firstTeamButton}
                        <button
                            type='button'
                            className='team-schedule-trigger'
                            aria-label={`View ${firstTeam} season schedule`}
                            onClick={(event) => { event.stopPropagation(); openScheduleModal(firstTeam); }}
                        >
                            <CalendarWeek/>
                        </button>
                    </div>
                    <div className='matchup-tile'>
                        {secondTeamButton}
                        <button
                            type='button'
                            className='team-schedule-trigger'
                            aria-label={`View ${secondTeam} season schedule`}
                            onClick={(event) => { event.stopPropagation(); openScheduleModal(secondTeam); }}
                        >
                            <CalendarWeek/>
                        </button>
                    </div>
                </div>
            )
        }
    }

    else if(locked[activePage-1] && matchupsArr.length > 1 && userPicks.length > 1) {
        let team = Object.keys(userPicks[activePage-1])[0];

        if(team.includes('Team')) {
            weeklyButtons.push(
                <div key={activePage+'-locked'}className='locked-pick-section'>
                    <Lock className='locked-tooltip-icon' />
                    Your pick is locked for Week {activePage}: None
                </div>
            )
        } else {
            var NFLTeam = teamIcons[Object.keys(userPicks[activePage-1])[0]];

            weeklyButtons.push(
                <div key={activePage+'-locked'}className='locked-pick-section'>
                    <Lock className='locked-tooltip-icon' />
                    Your pick is locked for Week {activePage}:
                    <span className='locked-pick-offset'>
                        <NFLTeam/>
                    </span>
                        
                </div>
            )
        }
    }

    function goToLogin() {
        navigate('/login')
    }

    if(!newSession) {
        return (
            <>
                <div className='login-button-section'>
                    <Button onClick={goToLogin}> Sign in </Button>
                </div>
            </>
        )
    }

    return (
        <>
            <div className='main-section'>
                <div className='page-header'>
                    <h1 className='page-title'>Enter Picks</h1>
                    <p className='page-subtitle'>Week {activePage}</p>
                </div>
                <div className='pick-meta-row'>
                    <DeadlineTooltip
                        weekNum={activePage}
                        year={year}
                        weekDeadlines={deadlinesArr.length > 1 ? deadlinesArr[activePage-1] : []}
                    />
                    <PickReminderSwitch
                        email={newSession['idToken']['payload']['email']}
                        currDecision={currReminderDecision}
                        setCurrDecision={setUserReminderDecision}
                    />
                </div>
                <PastPicksCard
                    stats={userStats}
                    picks={userPicks}
                />
                <TeamAvailabilityTracker
                    userPicks={userPicks}
                    games={fullSchedule}
                    currentWeek={serverCurrentWeek}
                    onSelectTeam={openScheduleModal}
                    onExpand={ensureFullSchedule}
                />
                <div className='team-section'>
                    
                    <Form onSubmit={submitUserPick}>
                        <ButtonToolbar aria-label='Toolbar with button groups'>
                            <ButtonGroup className='me-2 team-group' aria-label='First group'>
                                {
                                    matchupsArr.length <= 1 && userPicks.length <= 1
                                        ? Array.from({length: 4}).map((_, i) => (
                                            <div className='matchup-row' key={'skeleton-' + i}>
                                                <div className='skeleton pick-select-skeleton' />
                                                <div className='skeleton pick-select-skeleton' />
                                            </div>
                                        ))
                                        : arrButtons
                                }
                                <p className='breaker'/>
                                {/* <Button type='submit' disabled={!isTeamSelectedArr[activePage-1]}> Submit </Button> */}
                                {
                                    !locked[activePage-1] && showSubmitButton
                                        ? <Button type='submit' disabled={isWeekNormalDeadlinePassed(activePage)}> Submit </Button>
                                        : ''
                                }
                                    
                            </ButtonGroup>
                        </ButtonToolbar>
                    </Form>
                    {
                        weeklyButtons
                    }
                    {
                         showSubmitButton
                         ? <Pagination className='pagination-enter-picks'>{items}</Pagination>
                         : ''
                    }
                    
                </div>
                <AlertModal
                    show={showAlertModal[0]}
                    onHide={()=>{setShowAlertModal([false, '', ''])}}
                    modalTitle={showAlertModal[2]}
                    modalBody={showAlertModal[1]}
                />
                <TeamScheduleModal
                    show={scheduleModalTeam != null}
                    onHide={() => setScheduleModalTeam(null)}
                    team={scheduleModalTeam}
                    games={fullSchedule}
                />
            </div>
        </>
    )
}

export default EnterPicks;